import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import {
  Activity, Heart, Thermometer, Wind, Droplets, Scale,
  Calendar, Shield, CheckCircle2, AlertTriangle, Plus, Loader2, Stethoscope,
} from "lucide-react";

const BAND_COLOR = {
  normal:   "#4ADE80",
  low:      "#60A5FA",
  elevated: "#FBBF24",
  high:     "#FB923C",
  critical: "#F87171",
};

function Field({ label, children }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">{label}</span>
      {children}
    </label>
  );
}

export default function GeneralHealth() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTabState] = useState(searchParams.get("tab") || "vitals");
  const setTab = (t) => {
    setTabState(t);
    const next = new URLSearchParams(searchParams);
    next.set("tab", t);
    setSearchParams(next, { replace: true });
  };
  useEffect(() => {
    const u = searchParams.get("tab");
    if (u && u !== tab) setTabState(u);
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const { user, viewingPatient } = useAuth();
  // Age/sex come from the profile of the currently-scoped patient. Providers
  // viewing get them from viewingPatient; patients from their own user object.
  const patient = viewingPatient || user || {};
  const [age, setAge] = useState(patient.age || 55);
  const [sex, setSex] = useState((patient.sex || "female").toLowerCase());
  const [smoker, setSmoker] = useState(false);
  const [packYears, setPackYears] = useState(0);

  const [snapshot, setSnapshot] = useState(null);
  const [vitalsHistory, setVitalsHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ age: String(age), sex,
        smoker: String(smoker), smoker_pack_years: String(packYears) });
      if (snapshot?.latest_vitals?.bmi?.bmi)
        params.set("bmi", String(snapshot.latest_vitals.bmi.bmi));
      const [snapRes, vRes] = await Promise.all([
        api.get(`/general/snapshot?${params.toString()}`),
        api.get("/general/vitals"),
      ]);
      setSnapshot(snapRes.data);
      setVitalsHistory(vRes.data.vitals || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load General Health");
    } finally {
      setLoading(false);
    }
  }, [age, sex, smoker, packYears]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadAll(); }, [loadAll]);

  const overdueScreenings = useMemo(
    () => (snapshot?.screenings || []).filter((s) => s.is_overdue),
    [snapshot]
  );
  const dueImms = useMemo(
    () => (snapshot?.immunizations || []).filter((i) => i.is_due),
    [snapshot]
  );

  return (
    <div className="space-y-6" data-testid="general-health-page">
      <div className="ros-card flex items-center gap-4">
        <div className="w-12 h-12 rounded-2xl bg-[#4ADE80]/10 border border-[#4ADE80]/30 flex items-center justify-center">
          <Stethoscope className="text-[#4ADE80]" size={22} />
        </div>
        <div className="flex-1">
          <div className="font-heading text-xl">General Health</div>
          <div className="text-xs text-[#A3ACA7] mt-0.5">
            Specialty-agnostic primary-care baseline: vitals, USPSTF screenings, CDC 2026 immunisations.
          </div>
        </div>
        <div className="flex items-center gap-4 text-xs">
          <div className="text-center">
            <div className="text-2xl font-heading" style={{ color: overdueScreenings.length ? "#FB923C" : "#4ADE80" }}>
              {overdueScreenings.length}
            </div>
            <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">Overdue screens</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-heading" style={{ color: dueImms.length ? "#FB923C" : "#4ADE80" }}>
              {dueImms.length}
            </div>
            <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">Due vaccines</div>
          </div>
        </div>
      </div>

      {/* Demographics panel */}
      <div className="ros-card">
        <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7] mb-3">
          Screening context (updates recommendations)
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Field label="Age">
            <input type="number" min={0} max={120} value={age}
                   onChange={(e) => setAge(Number(e.target.value))}
                   data-testid="gh-age" className="ros-input" />
          </Field>
          <Field label="Sex">
            <select value={sex} onChange={(e) => setSex(e.target.value)}
                    data-testid="gh-sex" className="ros-input">
              <option value="female">Female</option>
              <option value="male">Male</option>
            </select>
          </Field>
          <Field label="Smoker">
            <select value={smoker ? "yes" : "no"}
                    onChange={(e) => setSmoker(e.target.value === "yes")}
                    data-testid="gh-smoker" className="ros-input">
              <option value="no">No</option>
              <option value="yes">Yes</option>
            </select>
          </Field>
          <Field label="Pack-years">
            <input type="number" min={0} value={packYears}
                   onChange={(e) => setPackYears(Number(e.target.value))}
                   data-testid="gh-pack-years" className="ros-input" />
          </Field>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2" data-testid="gh-tabs">
        {[
          { key: "vitals", label: "Vitals", icon: Activity },
          { key: "screenings", label: "Screenings (USPSTF)", icon: Shield },
          { key: "immunizations", label: "Immunisations (CDC)", icon: CheckCircle2 },
        ].map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
                    data-testid={`gh-tab-${t.key}`}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      active ? "bg-[#4ADE80] text-black" : "bg-[#1C201E] text-[#A3ACA7] hover:text-white"
                    }`}>
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {loading && (
        <div className="ros-card flex items-center gap-2 text-sm text-[#A3ACA7]">
          <Loader2 className="animate-spin" size={14} /> Loading…
        </div>
      )}

      {!loading && tab === "vitals" &&
        <VitalsTab snapshot={snapshot} history={vitalsHistory} onSaved={loadAll} />}
      {!loading && tab === "screenings" &&
        <ScreeningsTab screenings={snapshot?.screenings || []} onCompleted={loadAll} />}
      {!loading && tab === "immunizations" &&
        <ImmunizationsTab immunizations={snapshot?.immunizations || []} records={snapshot?.immunizations || []} onSaved={loadAll} />}
    </div>
  );
}

/* ================= Vitals ================= */
function VitalsTab({ snapshot, history, onSaved }) {
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    systolic_bp: "", diastolic_bp: "", heart_rate: "",
    respiratory_rate: "", temperature_c: "", spo2: "",
    weight_kg: "", height_cm: "", pain_score: "", notes: "",
  });
  const [saving, setSaving] = useState(false);

  async function submit(e) {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { date: form.date };
      ["systolic_bp","diastolic_bp","heart_rate","respiratory_rate",
       "temperature_c","spo2","weight_kg","height_cm","pain_score"].forEach((k) => {
        if (form[k] !== "" && form[k] != null)
          payload[k] = k === "pain_score" ? Number(form[k]) : Number(form[k]);
      });
      if (form.notes) payload.notes = form.notes;
      await api.post("/general/vitals", payload);
      toast.success("Vitals recorded");
      setForm({ ...form, systolic_bp: "", diastolic_bp: "", heart_rate: "",
        respiratory_rate: "", temperature_c: "", spo2: "", weight_kg: "",
        height_cm: "", pain_score: "", notes: "" });
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  }

  const latest = snapshot?.latest_vitals;
  const bands = latest?.bands || {};

  const chart = useMemo(() => history
    .slice().reverse()
    .filter((r) => r.systolic_bp && r.diastolic_bp)
    .map((r) => ({
      date: (r.date || "").slice(5),
      systolic: r.systolic_bp, diastolic: r.diastolic_bp,
    })), [history]);

  return (
    <div className="space-y-6" data-testid="gh-vitals-tab">
      {/* Latest vital cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <VitalCard label="Blood pressure" icon={Heart} band={bands.blood_pressure}
                   testId="vital-bp" />
        <VitalCard label="Heart rate" icon={Activity} band={bands.heart_rate}
                   testId="vital-hr" />
        <VitalCard label="Temperature" icon={Thermometer} band={bands.temperature}
                   testId="vital-temp" />
        <VitalCard label="SpO₂" icon={Wind} band={bands.spo2}
                   testId="vital-spo2" />
        <VitalCard label="Respiratory rate" icon={Wind} band={bands.respiratory_rate}
                   testId="vital-rr" />
        <VitalCard label="BMI" icon={Scale}
                   band={latest?.bmi ? { label: latest.bmi.label, band: latest.bmi.band, value: `${latest.bmi.bmi}` } : undefined}
                   testId="vital-bmi" />
      </div>

      {/* Add form */}
      <form onSubmit={submit} className="ros-card space-y-3" data-testid="vitals-form">
        <div className="font-heading text-lg">Record vitals</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Field label="Date"><input type="date" value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
            data-testid="vitals-date" className="ros-input" required /></Field>
          <Field label="Systolic BP (mmHg)"><input type="number" value={form.systolic_bp}
            onChange={(e) => setForm({ ...form, systolic_bp: e.target.value })}
            data-testid="vitals-sbp" className="ros-input" /></Field>
          <Field label="Diastolic BP (mmHg)"><input type="number" value={form.diastolic_bp}
            onChange={(e) => setForm({ ...form, diastolic_bp: e.target.value })}
            data-testid="vitals-dbp" className="ros-input" /></Field>
          <Field label="Heart rate (bpm)"><input type="number" value={form.heart_rate}
            onChange={(e) => setForm({ ...form, heart_rate: e.target.value })}
            data-testid="vitals-hr" className="ros-input" /></Field>
          <Field label="Resp rate (/min)"><input type="number" value={form.respiratory_rate}
            onChange={(e) => setForm({ ...form, respiratory_rate: e.target.value })}
            data-testid="vitals-rr" className="ros-input" /></Field>
          <Field label="Temp (°C)"><input type="number" step="0.1" value={form.temperature_c}
            onChange={(e) => setForm({ ...form, temperature_c: e.target.value })}
            data-testid="vitals-temp" className="ros-input" /></Field>
          <Field label="SpO₂ (%)"><input type="number" value={form.spo2}
            onChange={(e) => setForm({ ...form, spo2: e.target.value })}
            data-testid="vitals-spo2" className="ros-input" /></Field>
          <Field label="Pain (0-10)"><input type="number" min={0} max={10} value={form.pain_score}
            onChange={(e) => setForm({ ...form, pain_score: e.target.value })}
            data-testid="vitals-pain" className="ros-input" /></Field>
          <Field label="Weight (kg)"><input type="number" step="0.1" value={form.weight_kg}
            onChange={(e) => setForm({ ...form, weight_kg: e.target.value })}
            data-testid="vitals-weight" className="ros-input" /></Field>
          <Field label="Height (cm)"><input type="number" value={form.height_cm}
            onChange={(e) => setForm({ ...form, height_cm: e.target.value })}
            data-testid="vitals-height" className="ros-input" /></Field>
        </div>
        <button type="submit" disabled={saving} data-testid="vitals-submit"
                className="flex items-center gap-2 px-5 py-2 rounded-full bg-[#4ADE80] text-black font-semibold disabled:opacity-50">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          {saving ? "Saving…" : "Record vitals"}
        </button>
      </form>

      {/* Chart — use fixed width to avoid Recharts measure warning */}
      {chart.length >= 2 && (
        <div className="ros-card" data-testid="vitals-chart">
          <div className="font-heading text-lg mb-3">Blood pressure trend</div>
          <div className="w-full overflow-x-auto">
            <LineChart width={720} height={220} data={chart}
                       margin={{ top: 10, right: 16, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#272E2B" />
              <XAxis dataKey="date" stroke="#A3ACA7" fontSize={11} />
              <YAxis stroke="#A3ACA7" fontSize={11} />
              <Tooltip contentStyle={{ background: "#0F1312", border: "1px solid #272E2B", borderRadius: 12 }} />
              <Line type="monotone" dataKey="systolic" stroke="#F87171" strokeWidth={2} name="Systolic" dot={{ r: 3 }} />
              <Line type="monotone" dataKey="diastolic" stroke="#60A5FA" strokeWidth={2} name="Diastolic" dot={{ r: 3 }} />
            </LineChart>
          </div>
        </div>
      )}
    </div>
  );
}

function VitalCard({ label, icon: Icon, band, testId }) {
  const c = band ? BAND_COLOR[band.band] || "#A3ACA7" : "#4b5551";
  return (
    <div className="ros-card p-4" data-testid={testId}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#A3ACA7]">
        <Icon size={12} /> {label}
      </div>
      {band ? (
        <>
          <div className="text-2xl font-heading font-bold mt-1" style={{ color: c }}>
            {band.value}
          </div>
          <div className="text-[11px] font-semibold" style={{ color: c }}>{band.label}</div>
        </>
      ) : (
        <div className="text-sm text-[#7A8480] mt-2">Not recorded</div>
      )}
    </div>
  );
}

/* ================= Screenings ================= */
function ScreeningsTab({ screenings, onCompleted }) {
  const [busyId, setBusyId] = useState(null);

  async function markComplete(s) {
    setBusyId(s.id);
    try {
      await api.post("/general/screenings/complete", {
        screening_id: s.id,
        completed_date: new Date().toISOString().slice(0, 10),
        result: "normal",
      });
      toast.success(`${s.title} recorded`);
      onCompleted();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusyId(null); }
  }

  if (!screenings.length) {
    return <div className="ros-card text-sm text-[#A3ACA7]" data-testid="screenings-empty">
      No USPSTF screenings apply to this demographic — you&apos;re outside all the age/sex/risk gates.
    </div>;
  }

  const overdue = screenings.filter((s) => s.is_overdue);
  const upToDate = screenings.filter((s) => !s.is_overdue);

  return (
    <div className="space-y-6" data-testid="gh-screenings-tab">
      {overdue.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#FB923C] mb-2 flex items-center gap-2">
            <AlertTriangle size={12} /> Overdue ({overdue.length})
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {overdue.map((s) => (
              <ScreeningCard key={s.id} s={s} busy={busyId === s.id}
                             onComplete={markComplete} overdue />
            ))}
          </div>
        </div>
      )}
      {upToDate.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#4ADE80] mb-2 flex items-center gap-2">
            <CheckCircle2 size={12} /> Up to date ({upToDate.length})
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {upToDate.map((s) => (
              <ScreeningCard key={s.id} s={s} busy={busyId === s.id}
                             onComplete={markComplete} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ScreeningCard({ s, busy, onComplete, overdue }) {
  const c = overdue ? "#FB923C" : "#4ADE80";
  return (
    <div className="ros-card space-y-2" data-testid={`screening-${s.id}`}
         style={{ borderColor: overdue ? "#FB923C30" : undefined }}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="font-heading text-base leading-tight">{s.title}</div>
          <div className="text-[10px] uppercase tracking-wider text-[#7A8480] mt-1">
            USPSTF grade {s.uspstf_grade} · Every {s.interval_years >= 999 ? "one-time" : `${s.interval_years}y`}
          </div>
        </div>
        <div className="text-[10px] font-bold px-2 py-0.5 rounded-full"
             style={{ color: c, background: `${c}18`, border: `1px solid ${c}40` }}>
          {overdue ? "Overdue" : "Current"}
        </div>
      </div>
      <div className="text-xs text-[#A3ACA7]">
        Options: {(s.modalities || []).join(" · ")}
      </div>
      {s.rationale && (
        <div className="text-[11px] text-[#7A8480] italic">{s.rationale}</div>
      )}
      {s.last_completed && (
        <div className="text-[11px] text-[#A3ACA7]">Last: {s.last_completed}</div>
      )}
      <button onClick={() => onComplete(s)} disabled={busy}
              data-testid={`screening-complete-${s.id}`}
              className="mt-1 flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[#1C201E] hover:bg-[#272E2B] text-white transition-colors disabled:opacity-50">
        {busy ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
        Mark completed today
      </button>
    </div>
  );
}

/* ================= Immunisations ================= */
function ImmunizationsTab({ immunizations, onSaved }) {
  const [busyId, setBusyId] = useState(null);

  async function recordDose(imm) {
    setBusyId(imm.id);
    try {
      await api.post("/general/immunizations", {
        immunization_id: imm.id,
        date: new Date().toISOString().slice(0, 10),
      });
      toast.success(`${imm.name} recorded`);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusyId(null); }
  }

  const due = immunizations.filter((i) => i.is_due);
  const upToDate = immunizations.filter((i) => !i.is_due);

  return (
    <div className="space-y-6" data-testid="gh-immunizations-tab">
      {due.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#FB923C] mb-2 flex items-center gap-2">
            <AlertTriangle size={12} /> Due now ({due.length})
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {due.map((i) => (
              <ImmCard key={i.id} imm={i} busy={busyId === i.id}
                       onRecord={recordDose} due />
            ))}
          </div>
        </div>
      )}
      {upToDate.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#4ADE80] mb-2 flex items-center gap-2">
            <CheckCircle2 size={12} /> On schedule ({upToDate.length})
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {upToDate.map((i) => (
              <ImmCard key={i.id} imm={i} busy={busyId === i.id}
                       onRecord={recordDose} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ImmCard({ imm, busy, onRecord, due }) {
  const c = due ? "#FB923C" : "#4ADE80";
  return (
    <div className="ros-card space-y-2" data-testid={`imm-${imm.id}`}
         style={{ borderColor: due ? "#FB923C30" : undefined }}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="font-heading text-base leading-tight">{imm.name}</div>
          <div className="text-[10px] uppercase tracking-wider text-[#7A8480] mt-1">
            {imm.cadence}
          </div>
        </div>
        <div className="text-[10px] font-bold px-2 py-0.5 rounded-full"
             style={{ color: c, background: `${c}18`, border: `1px solid ${c}40` }}>
          {due ? "Due" : "OK"}
        </div>
      </div>
      {imm.notes && <div className="text-[11px] text-[#7A8480] italic">{imm.notes}</div>}
      {imm.last_administered && (
        <div className="text-[11px] text-[#A3ACA7]">
          Last dose: {imm.last_administered} · Doses recorded: {imm.dose_count}
        </div>
      )}
      <button onClick={() => onRecord(imm)} disabled={busy}
              data-testid={`imm-record-${imm.id}`}
              className="mt-1 flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[#1C201E] hover:bg-[#272E2B] text-white transition-colors disabled:opacity-50">
        {busy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
        Record dose today
      </button>
    </div>
  );
}
